import { useEffect, useRef } from 'react';
import { useAuth } from '@shared/components/AuthProvider';
import { useHistory } from '@shared/core';
import { useRouterHelper } from '@shared/core/router.helper';
import { useQueryParamHelper } from '@shared/core/queryString';

/**
 * Post-hydration redirect for routes that should only be visible to
 * logged-out users (create-account, sign-in, forgot-password).
 *
 * Unlike CreateWeddingGuards with authMode="allowOnlyUnauthenticated",
 * this does NOT block render while auth is resolving. The form paints
 * immediately; the ~2.7% of visitors who are already logged in see it
 * briefly before the redirect fires. This is the trade needed for SSR
 * to deliver real form HTML instead of a skeleton placeholder.
 *
 * The didRedirectRef guards against repeat history.replace() calls
 * caused by the useRouterHelper/useQueryParamHelper dep identities
 * changing on every render.
 *
 * `formPath` is passed in by the caller (rather than imported from
 * route.paths) to avoid a module-level circular import.
 */
export const useRedirectAuthenticatedAway = (formPath: string) => {
  const { isCurrentUserSettled, isLoggedIn } = useAuth();
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const queryParamHelper = useQueryParamHelper();
  const didRedirectRef = useRef(false);

  useEffect(() => {
    if (didRedirectRef.current) return;
    if (!isCurrentUserSettled || !isLoggedIn) return;
    didRedirectRef.current = true;
    const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(formPath));
    history.replace(path);
  }, [isCurrentUserSettled, isLoggedIn, history, routeHelpers, queryParamHelper, formPath]);
};
